import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { Chip, ClickAwayListener, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DropDownPaper } from '../../../components/book/dropDownPaper';
import { TicketGroupTEType } from '../book';

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
  },
  sortChip: {
    color: 'inherit',
    border: '1px solid rgba(107, 107, 107, 0.6)',
    paddingLeft: '13px',
    '.MuiChip-icon': {
      marginRight: 0,
      marginLeft: 0,
    },
    '.MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    ':hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  sortChipWithGlow: {
    color: 'inherit',
    paddingLeft: '13px',
    animation: 'pulseGlow 2s ease-in-out infinite',
    '.MuiChip-icon': {
      marginRight: 0,
      marginLeft: 0,
    },
    '.MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    ':hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.secondary.dark,
    },
    '@keyframes pulseGlow': {
      '0%, 100%': {
        boxShadow: '0 0 5px rgba(255, 191, 0, 0.5), 0 0 10px rgba(255, 191, 0, 0.3), 0 0 15px rgba(255, 191, 0, 0.2)',
        border: '1px solid rgba(255, 191, 0, 0.6)',
      },
      '50%': {
        boxShadow: '0 0 20px rgba(255, 191, 0, 0.8), 0 0 30px rgba(255, 191, 0, 0.6), 0 0 40px rgba(255, 191, 0, 0.4)',
        border: '1px solid rgba(255, 191, 0, 0.9)',
      },
    },
  },
  sortTitle: {
    padding: '0 8px',
    fontSize: '0.75rem',
    paddingLeft: '3px',
    paddingRight: '20px',
    [theme.breakpoints.down(425)]: {
      fontSize: '0.625rem',
    },
  },
}));

export const TicketsSelector: FC<TicketsSelectorType> = memo(({ ticketGroup, handleTicketsCount, setTitle, shouldGlow, scrollToFooter }) => {
  const { classes } = useStyles();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedQuantityTickets, setSelectedQuantityTickets] = useState<number>(null);
  const [showGlow, setShowGlow] = useState(false);
  const ref = useRef();

  const handleOpenClick = () => {
    setIsOpen((open) => !open);
    if (!open) {
      // Убираем свечение, когда пользователь открывает список
      setShowGlow(false);
    }
  };

  const handleAwayClick = () => setIsOpen(false);

  useEffect(() => {
    handleTicketsCount(selectedQuantityTickets, ticketGroup);
    setTitle(
      selectedQuantityTickets
        ? `${selectedQuantityTickets} tickets by $${ticketGroup.retail_price} USD each`
        : `Choose the number of tickets`
    );
    // Убираем свечение, когда количество выбрано
    if (selectedQuantityTickets) {
      setShowGlow(false);
    }
  }, [selectedQuantityTickets]);

  useEffect(() => {
    // Включаем свечение, когда панель только что открылась и количество еще не выбрано
    if (shouldGlow && !selectedQuantityTickets) {
      setShowGlow(true);
      // Отключаем свечение через 10 секунд, чтобы не мешать пользователю
      const timer = setTimeout(() => {
        setShowGlow(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [shouldGlow, selectedQuantityTickets]);

  const handleQuantitySelect = (selectTitle: number) => {
    setSelectedQuantityTickets(selectTitle);
    setIsOpen(false);
    setShowGlow(false);
    // Прокручиваем к кнопке оплаты после выбора количества
    if (scrollToFooter) {
      scrollToFooter();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleAwayClick} ref={ref.current}>
      <Grid className={classes.container}>
        <Chip
          className={showGlow ? classes.sortChipWithGlow : classes.sortChip}
          label={
            <Typography component={'span'} className={classes.sortTitle}>
              {selectedQuantityTickets ? `${selectedQuantityTickets} tickets` : `Quantity`}
            </Typography>
          }
          icon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={handleOpenClick}
        />
        {isOpen && (
          <DropDownPaper
            ref={ref}
            data={ticketGroup.splits}
            selectTitle={selectedQuantityTickets}
            onChangeClick={handleQuantitySelect}
          />
        )}
      </Grid>
    </ClickAwayListener>
  );
});

type TicketsSelectorType = {
  ticketGroup: TicketGroupTEType;
  handleTicketsCount?: (count: number, ticketGroup: TicketGroupTEType) => void;
  setTitle: (title: string) => void;
  shouldGlow?: boolean;
  scrollToFooter?: () => void;
};
